import { NavigationProp, useNavigation } from '@react-navigation/native';
import { Button } from 'aimo-ui';
import ParkingSignInButton from 'components/park/ParkingSignInButton';
import {
  FooterButtonContainer,
  VerticalDistributedContainer,
} from 'components/sheets/SheetLayout';
import AccountContext from 'context/AccountContext';
import ParkingContext from 'context/ParkingContext';
import { differenceInMinutes, startOfMinute } from 'date-fns';
import useAddPaymentMethod from 'hooks/useAddPaymentMethod';
import useCreateSession from 'hooks/useCreateSession';
import useHasParkingSession from 'hooks/useHasParkingSession';
import useIsLoggedIn from 'hooks/useIsLoggedIn';
import useOngoingChargingSession from 'hooks/useOngoingChargingSession';
import useSelectedParkingVehicle from 'hooks/useSelectedParkingVehicle';
import useSelectedPaymentMethod from 'hooks/useSelectedPaymentMethod';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { onboardingCompleted } from 'states/persistInStorage';
import { useAuthStore } from 'stores/authStore';
import { useParkingStore } from 'stores/parkingStore';
import { SimpleFullContainer } from 'styles/ContainerStyles';
import { Vehicle } from 'types/generatedSchemaTypes';
import { ParkNavProps, RootNavProps } from 'types/navigation';
import { createAction } from 'utils/analytics';
import { isParkZoneChooseEndTime } from 'utils/navigationUtils';
import { getMissingVehicleTextKey } from 'utils/parkingButtonValidation';
import {
  arePaymentMethodsEqual,
  isCorporateAccountPaymentOption,
} from 'utils/paymentUtils';
import PaymentCardDropDown from '../detail/PaymentCardDropDown';
import { defineDisabled } from '../detail/StartParkingButton';
import VehicleSelectDropDown from '../detail/VehicleSelectDropDown';
import PreloadingWrapper from '../timeSelection/PreloadingWrapper';
import { useUiStore } from 'stores/uiStore';

const StartParkAndChargeButton: React.FC<any> = (...props) => {
  const { selectedEvCharger } = useContext(ParkingContext);

  const {
    parkingConditions,
    selectedParkingSegment,
    currentPaymentMethodId,
    selectedParkingVehiclePlate,
    setParkingConditions,
    setParkingVehiclePlate,
  } = useParkingStore((state) => state);

  const allowedTimes = {
    parkingAllowedFrom: selectedParkingSegment?.parkingAllowedFrom || '',
    parkingAllowedUntil: selectedParkingSegment?.parkingAllowedUntil || '',
  };
  const {
    selectedParkingZoneId,
    endTime,
    isValidEndTime,
    parkingZoneCountryCode,
  } = parkingConditions;

  const selectedVehicle = useSelectedParkingVehicle(
    false,
    undefined,
    selectedParkingVehiclePlate
  );
  const {
    customer: { vehicles },
  } = useContext(AccountContext);

  const paymentMethod = useSelectedPaymentMethod(currentPaymentMethodId);

  useEffect(() => {
    if (
      !arePaymentMethodsEqual(paymentMethod, parkingConditions.paymentMethod)
    ) {
      setParkingConditions({ paymentMethod });
    }
  }, [paymentMethod, parkingConditions.paymentMethod, setParkingConditions]);
  const { isLoggedIn, loginLoading } = useIsLoggedIn();

  const { t } = useTranslation();
  const { navigate } = useNavigation<RootNavProps>();
  const { navigate: navigatePark } = useNavigation<ParkNavProps>();
  const navigation: NavigationProp<any> = useNavigation();
  const isChooseTimeScreen = isParkZoneChooseEndTime(navigation);
  const {
    createParking,
    createCharging,
    createParkingLoading,
    createChargingLoading,
    parkingSuccess,
    chargingSuccess,
  } = useCreateSession({
    parkingZoneType: 'PARK_AND_CHARGE',
    onErrorCallback: () => navigatePark('park'),
  });
  const { ongoingChargingSession } = useOngoingChargingSession();
  const { addCard, loadingAddCard } = useAddPaymentMethod();

  const notConnected = selectedEvCharger?.evse.status !== 'OCCUPIED';

  const ongoingChargingChanged = useMemo(() => {
    return ongoingChargingSession;
  }, [ongoingChargingSession]);
  const prevSessions = useRef(ongoingChargingChanged);
  const chargingSessionChange = prevSessions.current !== ongoingChargingChanged;

  const isUserRestricted = useAuthStore((state) => state.accountRestricted);

  const { hasParkingSession } = useHasParkingSession({
    parkingZoneUid: selectedParkingZoneId,
    vehicle: selectedVehicle,
  });
  const hasVehicles = !!(vehicles && vehicles.length > 0);

  const hasValidPaymentMethod = !!(
    paymentMethod?.cardId ||
    paymentMethod?.benefitTargetUid ||
    isCorporateAccountPaymentOption(paymentMethod) ||
    paymentMethod?.method === 'MOBILEPAY'
  );

  const missingDetails = !hasValidPaymentMethod || !selectedVehicle;

  const isOnboardingIncomplete = useMemo(
    () => !hasValidPaymentMethod && !selectedVehicle && !onboardingCompleted(),
    [hasValidPaymentMethod, selectedVehicle]
  );

  const startParkingChargingLoading = useMemo(
    () =>
      createParkingLoading ||
      createChargingLoading ||
      (chargingSuccess && !chargingSessionChange),
    [
      createParkingLoading,
      createChargingLoading,
      chargingSuccess,
      chargingSessionChange,
    ]
  );

  const minuteDif = useMemo(() => {
    if (endTime) {
      return differenceInMinutes(endTime, new Date()) + 1;
    }
  }, [endTime]);
  const minuteDifLessThanTwo = !!minuteDif && minuteDif < 2;

  const disabled =
    startParkingChargingLoading ||
    !selectedEvCharger ||
    (isChooseTimeScreen &&
      defineDisabled({
        parkingZoneUid: selectedParkingZoneId,
        allowedTimes,
        isValidEndTime,
      }));

  // If user switches to vehicle which has parking session, skip CHOOSE_END_TIME
  useEffect(() => {
    if (isChooseTimeScreen && hasParkingSession && notConnected) {
      setParkingConditions({ endTime: undefined });
      navigatePark('chargeConnection', {
        parkingZoneUid: selectedParkingZoneId,
        chargingStationId: selectedEvCharger?.id || '',
      });
    }
  }, [
    hasParkingSession,
    setParkingConditions,
    notConnected,
    navigatePark,
    selectedParkingZoneId,
    isChooseTimeScreen,
    selectedEvCharger?.id,
  ]);

  const setVehicleState = useCallback(
    (vehicle?: Vehicle) => {
      // Only update if the vehicle actually changed
      if (vehicle?.licensePlate?.plateText !== selectedParkingVehiclePlate) {
        setParkingVehiclePlate(vehicle?.licensePlate?.plateText);
      }
    },
    [selectedParkingVehiclePlate, setParkingVehiclePlate]
  );

  const handleContinue = async () => {
    if (isUserRestricted) {
      useUiStore.getState().setSheetModal('accountRestricted');
    } else if (isOnboardingIncomplete) {
      // Redirect to onboarding flow
      navigate('onboardingModal', {
        screen: 'welcome',
      });
    } else if (!hasValidPaymentMethod) {
      // Redirect to add payment method screen
      addCard();
    } else if (!selectedVehicle) {
      handleNoVehicleSelected();
      // If user has no parking or end time, set current screen CHOOSE_END_TIME
    } else if (!hasParkingSession && (!endTime || minuteDifLessThanTwo)) {
      setParkingConditions({ endTime: startOfMinute(new Date()) });
      navigatePark('parkEndTime', { parkingZoneUid: selectedParkingZoneId });
      // If user has not connected the charger we wait for the charger to be connected
    } else if (notConnected) {
      navigatePark('chargeConnection', {
        parkingZoneUid: selectedParkingZoneId,
        chargingStationId: selectedEvCharger?.id || '',
      });
    } else {
      createAction('TAP', 'CREATE_PARK_AND_CHARGE_SESSION', {
        selectedParkingZoneId,
      });
      parkingSuccess || hasParkingSession
        ? await createCharging()
        : await createParking();
    }
  };

  const handleNoVehicleSelected = () => {
    if (hasVehicles) {
      // Open select vehicle modal
      useUiStore.getState().setSheetModal('parkingVehicle');
    } else {
      // Redirect to add vehicle screen
      navigate('addVehicleParking');
    }
  };

  const buttonText = () => {
    // Priority 1: No EV charger selected
    if (!selectedEvCharger) {
      return t('evCharging.selectCharger');
    }

    // Priority 2: Check for missing payment
    if (!hasValidPaymentMethod) {
      return t('onboarding.addPaymentMethod');
    }

    // Priority 3-4: Check for missing vehicle
    const vehicleTextKey = getMissingVehicleTextKey(
      selectedVehicle,
      hasVehicles
    );
    if (vehicleTextKey) {
      return t(vehicleTextKey);
    }

    // Priority 5: Default - continue
    return t('button.continue');
  };

  if (!isLoggedIn) {
    return (
      <ParkingSignInButton
        testID="park-zone-details-login"
        loading={loginLoading}
      />
    );
  }

  return (
    <FooterButtonContainer {...props} pb={15}>
      <VerticalDistributedContainer>
        <FooterButtonContainer pb={0}>
          <VehicleSelectDropDown
            currentVehiclePlate={selectedParkingVehiclePlate}
            setVehicleState={setVehicleState}
          />
          <PaymentCardDropDown
            parkingZoneCountryCode={parkingZoneCountryCode}
            currentPaymentMethodId={currentPaymentMethodId}
          />
        </FooterButtonContainer>
        <FooterButtonContainer>
          <SimpleFullContainer>
            <PreloadingWrapper
              opacitySecondItem
              firstItem={
                <Button
                  buttonType={missingDetails ? 'secondary' : 'primary'}
                  size="l"
                  loading={startParkingChargingLoading || loadingAddCard}
                  text={buttonText()}
                  testID="park-zone-start-park-and-charge-button"
                  disabled={disabled}
                  onPress={handleContinue}
                />
              }
              secondItem={
                <Button
                  buttonType={missingDetails ? 'secondary' : 'primary'}
                  size="l"
                  disabled={disabled}
                  loading
                  text={buttonText()}
                />
              }
            />
          </SimpleFullContainer>
        </FooterButtonContainer>
      </VerticalDistributedContainer>
    </FooterButtonContainer>
  );
};

export default StartParkAndChargeButton;
